import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaCheck, FaChevronDown, FaFilter } from 'react-icons/fa';

const extractItemProperty = (item, keyOrFn, fallback = '') => {
  if (!item) return fallback;
  if (typeof keyOrFn === 'function') return keyOrFn(item);
  if (typeof keyOrFn === 'string' && keyOrFn in item) return item[keyOrFn];
  return fallback;
};

/**
 * Modular filter dropdown component focused exclusively on filtering data options.
 * Supports single and multi-select modes, customizable key extractors, and outside-click dismissal.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.filterLabel='Filter Data'] - Button label text (can also use alias `title`).
 * @param {string} [props.title] - Alias for filterLabel.
 * @param {Array<Object>} [props.options=[]] - List of filter options (can also use alias `data`).
 * @param {Array<Object>} [props.data=[]] - Alias for options.
 * @param {string|Function} [props.valueKey='id'] - Property key or extractor function for the option value.
 * @param {string|Function} [props.labelKey='name'] - Property key or extractor function for the option display label.
 * @param {Array<string|number>} [props.selectedValues] - Controlled array of currently selected option values.
 * @param {Array<string|number>} [props.defaultSelectedValues=[]] - Initial selected values for uncontrolled usage.
 * @param {Function} [props.onSelectionChange] - Callback invoked when selection changes: `(selectedValues, selectedItems) => void`.
 * @param {Function} [props.onChange] - Alias for onSelectionChange.
 * @param {boolean} [props.isMultiSelect=true] - Allows multiple options to be selected when true (can also use alias `isMulti`).
 * @param {boolean} [props.isMulti] - Alias for isMultiSelect.
 * @param {string} [props.className=''] - Additional CSS classes applied to the root element.
 *
 * @example
 * <DataFilter
 *   filterLabel="Filter Status"
 *   options={statusOptions}
 *   valueKey="value"
 *   labelKey="label"
 *   selectedValues={selectedStatus}
 *   onSelectionChange={(values) => setSelectedStatus(values)}
 * />
 */
const DataFilter = ({
  filterLabel = 'Filter Data',
  title,
  options = [],
  data = [],
  valueKey = 'id',
  labelKey = 'name',
  selectedValues: controlledSelectedValues,
  defaultSelectedValues = [],
  onSelectionChange,
  onChange,
  isMultiSelect = true,
  isMulti,
  className = ''
}) => {
  const displayTitle = title || filterLabel;
  const dataOptions = options.length > 0 ? options : data;
  const isMultiSelection = isMulti !== undefined ? isMulti : isMultiSelect;
  const triggerChangeCallback = onSelectionChange || onChange;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isControlledSelection = controlledSelectedValues !== undefined;
  const [internalSelectedValues, setInternalSelectedValues] = useState(defaultSelectedValues);
  const activeSelectedValues = isControlledSelection
    ? controlledSelectedValues || []
    : internalSelectedValues;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const optionsByIdMap = useMemo(() => {
    const map = new Map();
    dataOptions.forEach((item) => {
      const val = extractItemProperty(item, valueKey);
      if (val !== undefined && val !== null) {
        map.set(val, item);
      }
    });
    return map;
  }, [dataOptions, valueKey]);

  const handleSelectionUpdate = (newSelectedValues) => {
    if (!isControlledSelection) {
      setInternalSelectedValues(newSelectedValues);
    }
    if (typeof triggerChangeCallback === 'function') {
      const selectedItems = newSelectedValues
        .map((val) => optionsByIdMap.get(val))
        .filter(Boolean);
      triggerChangeCallback(newSelectedValues, selectedItems);
    }
  };

  const toggleOptionSelection = (optionValue) => {
    if (!isMultiSelection) {
      const updated = activeSelectedValues.includes(optionValue) ? [] : [optionValue];
      handleSelectionUpdate(updated);
      setIsDropdownOpen(false);
      return;
    }

    if (activeSelectedValues.includes(optionValue)) {
      handleSelectionUpdate(activeSelectedValues.filter((val) => val !== optionValue));
    } else {
      handleSelectionUpdate([...activeSelectedValues, optionValue]);
    }
  };

  const selectAllOptions = () => {
    const allValues = dataOptions
      .map((item) => extractItemProperty(item, valueKey))
      .filter((val) => val !== undefined && val !== null);
    handleSelectionUpdate(allValues);
  };

  const clearAllSelections = () => {
    handleSelectionUpdate([]);
  };

  const selectedCount = activeSelectedValues.length;

  return (
    <div className={`relative inline-block ${className}`.trim()} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`inline-flex items-center gap-2.5 rounded-md border px-3.5 py-2 text-xs font-semibold transition-colors shadow-2xs focus:outline-none focus:ring-2 focus:ring-[#3B74BE]/20 cursor-pointer ${
          selectedCount > 0
            ? 'border-[#3B74BE] bg-blue-50 text-[#3B74BE]'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <FaFilter className={selectedCount > 0 ? 'text-[#3B74BE]' : 'text-gray-400'} />
        <span>{displayTitle}</span>
        {selectedCount > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-[#3B74BE] px-2 py-0.5 text-[10px] font-bold text-white">
            {selectedCount}
          </span>
        )}
        <FaChevronDown
          className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180 text-[#3B74BE]' : ''
          }`}
        />
      </button>

      {isDropdownOpen && (
        <div className="absolute left-0 z-50 mt-1.5 w-72 md:w-80 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
          <div className="p-2.5 border-b border-gray-200 bg-gray-50 rounded-t-md flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              Select Options ({dataOptions.length})
            </span>
            {isMultiSelection && (
              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={selectAllOptions}
                  className="text-[#3B74BE] font-medium hover:underline cursor-pointer"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={clearAllSelections}
                  className="text-gray-500 font-medium hover:text-red-600 hover:underline cursor-pointer"
                >
                  Reset
                </button>
              </div>
            )}
          </div>

          <div className="max-h-60 overflow-y-auto p-1 divide-y divide-gray-100">
            {dataOptions.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500">
                No options available.
              </div>
            ) : (
              dataOptions.map((item) => {
                const val = extractItemProperty(item, valueKey);
                const label = extractItemProperty(item, labelKey);
                const isChecked = activeSelectedValues.includes(val);

                return (
                  <div
                    key={val}
                    onClick={() => toggleOptionSelection(val)}
                    className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-md cursor-pointer transition-colors ${
                      isChecked
                        ? 'bg-blue-50 text-[#3B74BE] font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                        isChecked
                          ? 'border-[#3B74BE] bg-[#3B74BE] text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isChecked && <FaCheck className="text-[10px]" />}
                    </div>
                    <span className="truncate">{label}</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 bg-gray-50 rounded-b-md text-xs">
            <span className="text-gray-500">
              Selected: <strong className="text-gray-900">{selectedCount}</strong>
            </span>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(false)}
              className="rounded-md bg-[#3B74BE] px-3 py-1 text-xs font-medium text-white hover:bg-[#3263a4] transition-colors cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataFilter;
